import React, { useState, useEffect, useRef } from 'react';
// ⚠ NEW (Claude Design login handover, 2026-05-31; supplier accounts 2026-05-31)
// Unified login surface for BOTH audiences on one cinematic card:
//   • Internal team → real Google Identity Services sign-in (GIS).
//   • Supplier      → real email + password sign-in (api_login). Magic-link
//                     sign-in still works as a fallback. First password is set
//                     via the link emailed on approval (SetPasswordScreen).
// Microsoft SSO from the mockup is intentionally omitted — the backend only
// verifies Google ID tokens + HMAC magic links.
import { renderSignInButton, setVendorSession, consumeSessionExpired, consumeNotStaff } from '../auth.js';
import { api } from '../api.js';
import { useT } from '../i18n.js';
import { IconArrow, IconBuilding, IconCheck, IconLock, IconReceipt, IconShield } from './icons.jsx';

const GoogleG = () => (
  <svg className="sso-logo" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
  </svg>
);

/* The real GIS button (Google renders its own iframe button into the div). */
function GoogleSignInButton() {
  const ref = useRef(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    renderSignInButton(ref.current, {
      theme: 'filled_black',
      size: 'large',
      shape: 'pill',
      text: 'signin_with',
      logo_alignment: 'center',
      width: 320,
    }).catch(err => {
      console.warn('Google sign-in init failed:', err.message);
      setFailed(true);
    });
  }, []);
  return (
    <div className="gsi-wrap" ref={ref}>
      {failed && (
        <div className="sso-btn" style={{ cursor: 'default', opacity: 0.6 }}>
          <GoogleG/> Google sign-in unavailable
        </div>
      )}
    </div>
  );
}

const BrandRail = ({ eyebrow, title, body }) => {
  const t = useT();
  return (
  <div className="login-brand">
    <div className="lb-logos">
      <img className="mr" src="assets/logo-white.png" alt="MotionReach"/>
      <span className="vrule"></span>
      <img className="bs" src="assets/brandstorm-white.png" alt="Brandstorm"/>
    </div>
    <div className="lb-copy">
      <div className="lb-eyebrow">{eyebrow}</div>
      <h2>{title}</h2>
      <p>{typeof body === 'string' ? t(body) : body}</p>
    </div>
  </div>
  );
};

/* ============================================================
   LOGIN — internal Google SSO + supplier email/password
   props:
     initialMode      "internal" | "supplier"
     onForgot()       supplier "Forgot?" link
   On a successful supplier sign-in we persist the session token and reload —
   the app then renders the portal (matches the signOut reload pattern).
   ============================================================ */
export function LoginScreen({ initialMode = 'internal', onForgot = () => {} }) {
  const t = useT();
  // If we landed here because a session expired, reopen the matching login
  // surface ('supplier' | 'internal') and show a one-shot notice explaining why.
  const [expiredMode] = useState(() => consumeSessionExpired());
  // If a non-staff Google sign-in was just rejected, we bounced here — most
  // likely a supplier who used "Sign in with Google" instead of their emailed
  // link. Open in Supplier mode and explain.
  const [notStaff] = useState(() => consumeNotStaff());
  const [mode, setMode] = useState(notStaff ? 'supplier' : (expiredMode || initialMode));
  const isInternal = mode === 'internal';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleSupplierSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) { setError('Enter your email and password.'); return; }
    setBusy(true);
    try {
      const res = await api.login(email.trim(), password, remember);
      setVendorSession(res.token);
      window.location.reload();
    } catch (err) {
      setError(err.message || 'Sign-in failed.');
      setBusy(false);
    }
  };

  const brand = isInternal
    ? {
        eyebrow: 'Vendor OS · Workspace',
        title: <>Every supplier,<br/>every baht, one place.</>,
        body: 'The shared vendor system for MotionReach & Brandstorm teams — register suppliers, review invoices, and keep procurement clean.',
      }
    : {
        eyebrow: 'Vendor OS · Supplier Portal',
        title: <>One inbox for<br/>the work and the money.</>,
        body: 'Submit invoices, track every approval step, and get paid faster. We pay partners on real terms — and tell you exactly when.',
      };

  return (
    <div className="login-stage">
      <div className="login-card" data-theme="dark">
        <BrandRail {...brand}/>

        <div className="login-panel">
          <div className="lp-head">
            <h1>{isInternal ? t('Sign in to your workspace') : t('Welcome back')}</h1>
            <p className="sub">{isInternal ? t('Use your MotionReach or Brandstorm account') : t('Sign in to your supplier account')}</p>
          </div>

          <div className="login-seg" role="tablist">
            <button className={isInternal ? 'active' : ''} onClick={() => setMode('internal')}>
              <IconBuilding size={15} className="seg-ico"/> {t("Internal team")}
            </button>
            <button className={!isInternal ? 'active' : ''} onClick={() => setMode('supplier')}>
              <IconReceipt size={15} className="seg-ico"/> {t("Supplier")}
            </button>
          </div>

          {expiredMode && (
            <div className="login-note" style={{background:'color-mix(in oklab, var(--warning) 12%, transparent)', borderColor:'color-mix(in oklab, var(--warning) 30%, transparent)'}}>
              <IconLock size={16} style={{color:'var(--warning)'}}/>
              <span style={{color:'var(--warning)'}}>{t("Your session expired. Please sign in again.")}</span>
            </div>
          )}

          {notStaff && (
            <div className="login-note" style={{background:'color-mix(in oklab, var(--warning) 12%, transparent)', borderColor:'color-mix(in oklab, var(--warning) 30%, transparent)'}}>
              <IconShield size={16} style={{color:'var(--warning)'}}/>
              <span style={{color:'var(--warning)'}}>{t("That Google account isn't a registered staff member. If you're a supplier, open the sign-in link we emailed you — or sign in with your supplier email & password below.")}</span>
            </div>
          )}

          {isInternal ? (
            <>
              <GoogleSignInButton/>

              <div className="login-divider">{t("SSO restricted to company domains")}</div>

              <div className="login-foot">
                {t("Access is limited to")} <span className="mono" style={{color:'var(--text-secondary)'}}>@motionreach.co.th</span> {t("and")} <span className="mono" style={{color:'var(--text-secondary)'}}>@brandstorm.co.th</span>.<br/>
                {t("Need access? Ask an admin to invite you.")}
              </div>
            </>
          ) : (
            <form onSubmit={handleSupplierSubmit} style={{display:'flex', flexDirection:'column', gap:16}}>
              <div className="field">
                <label className="field-label">{t("Email")}</label>
                <input className="input" type="email" autoComplete="username"
                  placeholder="billing@yourcompany.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} autoFocus/>
              </div>

              <div className="field">
                <div style={{display:'flex', justifyContent:'space-between'}}>
                  <label className="field-label">{t("Password")}</label>
                  <a href="#" onClick={(e) => { e.preventDefault(); onForgot(); }} style={{fontSize:12, color:'var(--text-accent)', textDecoration:'none'}}>{t("Forgot?")}</a>
                </div>
                <input className="input" type="password" autoComplete="current-password"
                  placeholder="••••••••••"
                  value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy}/>
              </div>

              <label style={{display:'flex', alignItems:'center', gap:8, fontSize:13, color:'var(--text-secondary)', cursor:'pointer'}}>
                <input type="checkbox" className="cb" checked={remember} onChange={(e) => setRemember(e.target.checked)} disabled={busy}/>
                <span>{t("Keep me signed in on this device")}</span>
              </label>

              {error && (
                <div className="login-note" style={{background:'color-mix(in oklab, var(--danger) 10%, transparent)', borderColor:'color-mix(in oklab, var(--danger) 30%, transparent)'}}>
                  <IconLock size={16} style={{color:'var(--danger)'}}/>
                  <span style={{color:'var(--danger)'}}>{error}</span>
                </div>
              )}

              <button type="submit" className="btn btn-primary btn-lg" style={{width:'100%'}} disabled={busy}>
                {busy ? t('Signing in…') : <>{t("Sign in")} <IconArrow size={14}/></>}
              </button>

              <div className="login-foot">
                {t("First time here? Use the Create your password link from your approval email. Not registered yet? Ask your MotionReach contact to onboard you.")}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   FORGOT PASSWORD — supplier reset / new-link request
   Calls api_requestPasswordReset, which always succeeds (no account
   enumeration) and emails a 30-minute reset link to registered vendors.
   ============================================================ */
export function ForgotScreen({ onBack = () => {} }) {
  const t = useT();
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) { setError('Enter your email.'); return; }
    setBusy(true);
    try {
      await api.requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      setError(err.message || 'Could not send the reset email.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-stage">
      <div className="login-card" data-theme="dark">
        <BrandRail
          eyebrow="Account recovery"
          title={<>Locked out?<br/>Happens to the best of us.</>}
          body="Enter the email on file with MotionReach × Brandstorm and we'll send a secure reset link, valid for 30 minutes."
        />

        <div className="login-panel">
          {!sent ? (
            <form onSubmit={submit} style={{display:'flex', flexDirection:'column', gap:18}}>
              <div className="lp-head">
                <h1>{t("Reset your password")}</h1>
                <p className="sub">{t("We'll email you a link to set a new one.")}</p>
              </div>
              <div className="field">
                <label className="field-label">{t("Email")}</label>
                <input className="input" type="email" autoComplete="username" placeholder="billing@yourcompany.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} autoFocus/>
                <span className="field-help">{t("Must match your registered MotionReach account.")}</span>
              </div>
              {error && (
                <div className="login-note" style={{background:'color-mix(in oklab, var(--danger) 10%, transparent)', borderColor:'color-mix(in oklab, var(--danger) 30%, transparent)'}}>
                  <IconLock size={16} style={{color:'var(--danger)'}}/><span style={{color:'var(--danger)'}}>{error}</span>
                </div>
              )}
              <button type="submit" className="btn btn-primary btn-lg" style={{width:'100%'}} disabled={busy}>
                {busy ? t('Sending…') : <>{t("Send reset link")} <IconArrow size={14}/></>}
              </button>
              <button type="button" onClick={onBack} className="btn btn-ghost btn-md" style={{width:'100%'}} disabled={busy}>← {t("Back to sign in")}</button>
              <div className="login-foot">{t("Need help? Email")} <span style={{color:'var(--text-accent)'}}>support@motionreach.co.th</span></div>
            </form>
          ) : (
            <div style={{display:'flex', flexDirection:'column', gap:16, alignItems:'center', textAlign:'center'}}>
              <div style={{width:60, height:60, borderRadius:'50%', background:'color-mix(in oklab, var(--success) 18%, transparent)', color:'var(--success)', display:'inline-flex', alignItems:'center', justifyContent:'center'}}>
                <IconCheck size={30}/>
              </div>
              <div className="lp-head">
                <h1>{t("Check your inbox")}</h1>
                <p className="sub" style={{lineHeight:1.6}}>{t("If that email is registered, a reset link is on its way. It expires in 30 minutes.")}</p>
              </div>
              <button onClick={onBack} className="btn btn-primary btn-lg" style={{width:'100%'}}>{t("Back to sign in")}</button>
              <button onClick={() => { setSent(false); }} className="btn btn-ghost btn-sm">{t("Send again")}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   SET / RESET PASSWORD — reached via the ?pw=<token> link from the
   approval or password-reset email. Sets the supplier's password, then
   signs them straight in with the returned session token.
   ============================================================ */
export function SetPasswordScreen({ token, onCancel = () => {} }) {
  const t = useT();
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (pw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (pw !== confirm) { setError('Passwords do not match.'); return; }
    setBusy(true);
    try {
      const res = await api.setPasswordWithToken(token, pw);
      setVendorSession(res.token);
      window.location.reload();
    } catch (err) {
      setError(err.message || 'Could not set your password. The link may have expired.');
      setBusy(false);
    }
  };

  return (
    <div className="login-stage">
      <div className="login-card" data-theme="dark">
        <BrandRail
          eyebrow="Vendor OS · Supplier Portal"
          title={<>Create your<br/>password.</>}
          body="Choose a password for your MotionReach vendor account. You'll use your email and this password to sign in from now on."
        />
        <div className="login-panel">
          <form onSubmit={submit} style={{display:'flex', flexDirection:'column', gap:16}}>
            <div className="lp-head">
              <h1>{t("Set your password")}</h1>
              <p className="sub">{t("At least 8 characters.")}</p>
            </div>
            <div className="field">
              <label className="field-label">{t("New password")}</label>
              <input className="input" type="password" autoComplete="new-password" placeholder="••••••••••"
                value={pw} onChange={(e) => setPw(e.target.value)} disabled={busy} autoFocus/>
            </div>
            <div className="field">
              <label className="field-label">{t("Confirm password")}</label>
              <input className="input" type="password" autoComplete="new-password" placeholder="••••••••••"
                value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={busy}/>
            </div>
            {error && (
              <div className="login-note" style={{background:'color-mix(in oklab, var(--danger) 10%, transparent)', borderColor:'color-mix(in oklab, var(--danger) 30%, transparent)'}}>
                <IconLock size={16} style={{color:'var(--danger)'}}/><span style={{color:'var(--danger)'}}>{error}</span>
              </div>
            )}
            <button type="submit" className="btn btn-primary btn-lg" style={{width:'100%'}} disabled={busy}>
              {busy ? t('Saving…') : <>{t("Set password & sign in")} <IconArrow size={14}/></>}
            </button>
            <button type="button" onClick={onCancel} className="btn btn-ghost btn-md" style={{width:'100%'}} disabled={busy}>← {t("Back to sign in")}</button>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   PORTAL WELCOME — magic-link confirmed; pick "enter portal".
   Same two-column cinematic card as the normal login.
   ============================================================ */
export function PortalWelcomeScreen({ vendor, onContinue, onSignOut }) {
  const t = useT();
  const initials = (vendor?.name || 'V').split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  return (
    <div className="login-stage">
      <div className="login-card" data-theme="dark">
        <BrandRail
          eyebrow="Vendor OS · Supplier Portal"
          title={<>One inbox for<br/>the work and the money.</>}
          body="Submit invoices, track every approval step, and get paid faster. We pay partners on real terms — and tell you exactly when."
        />
        <div className="login-panel">
          <div className="lp-head">
            <h1>{vendor ? t("Welcome back, {name}", { name: vendor.name }) : t('Loading your account…')}</h1>
            <p className="sub">{vendor ? t("Confirm it's you and head into your portal.") : t('One moment…')}</p>
          </div>
          {vendor && (
            <div className="id-chip" style={{cursor:'default'}}>
              <span className="avatar avatar-sm">{initials}</span>
              <div className="id-meta">
                <div className="id-name">{vendor.name}</div>
                <div className="id-email mono">{vendor.vendor_id}{vendor.email ? ` · ${vendor.email}` : ''}</div>
              </div>
            </div>
          )}
          <button className="btn btn-primary btn-lg" style={{width:'100%'}} disabled={!vendor} onClick={onContinue}>
            {vendor ? <>{t("Enter your portal")} <IconArrow size={14}/></> : t('Loading…')}
          </button>
          <div className="login-foot">
            {t("Not you?")} <a href="#" onClick={(e) => { e.preventDefault(); onSignOut(); }}>{t("Sign out")}</a>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   PORTAL NEEDS LINK — supplier arrived with no magic link.
   Same two-column cinematic card as the normal login.
   ============================================================ */
export function PortalNeedsLinkScreen({ onSwitchToStaff }) {
  const t = useT();
  return (
    <div className="login-stage">
      <div className="login-card" data-theme="dark">
        <BrandRail
          eyebrow="Vendor OS · Supplier Portal"
          title={<>One inbox for<br/>the work and the money.</>}
          body="Submit invoices, track every approval step, and get paid faster. We pay partners on real terms — and tell you exactly when."
        />
        <div className="login-panel">
          <div className="lp-head">
            <h1>{t("Open your secure link")}</h1>
            <p className="sub">{t("Suppliers sign in through the link we email you.")}</p>
          </div>
          <div className="login-note">
            <IconShield size={16}/>
            <span>{t("No password needed — your sign-in link looks like")} <span className="mono" style={{color:'var(--text-secondary)'}}>…?t=VND-1042.…</span>. {t("Lost it? Reply to any MotionReach email or contact your project manager for a fresh one.")}</span>
          </div>
          <div className="login-foot">
            {t("Internal team?")} <a href="#" onClick={(e) => { e.preventDefault(); onSwitchToStaff(); }}>{t("Sign in with Google")}</a>
          </div>
        </div>
      </div>
    </div>
  );
}
